// HouseholdItemsForm refactored: uses 'householditems' endpoints and single moveInDate.
import React, { useState, useEffect } from 'react';
import * as yup from 'yup';
import ApiProvider from '../../../apiProvider';
import { useMsal } from '@azure/msal-react';
import { CdsContentPanel } from 'clayco-component-library';
import { CdsDataTable } from 'clayco-component-library/lib';
import HouseholdItemsPolicyPanel from './components/HouseholdItemsPolicyPanel';
import PendingApprovalsList from './components/PendingApprovalsList';
import MySubmissionsList from './components/MySubmissionsList';
import HouseholdItemsModalForm from './components/HouseholdItemsModalForm';
import { useEmployeeNumber } from '../../../employeeNumber';
import { isSelfApproval, SELF_APPROVAL_ERROR_MSG, notifySelfApproval } from '../../../utility/selfApprovalCheck';
import { buildProjectOptionLabel } from '../../../utility/projectCompany';

// No duration logic needed for single move-in date.

const HouseholdItemsForm = ({ setMenuOptions }) => {
  const [showModal, setShowModal] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [formData, setFormData] = useState({ projectAssignment: [], moveInDate: '' });
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const activeAccount = msalAccounts?.[0];
  const { employeeNumber } = useEmployeeNumber();

  // Approval history state
  const historyGridColumns = ['Employee', 'Job', 'Submitted', 'Move In Date', 'Approver', 'Reviewed On', 'Is Approved?'];
  const historyDataKeys = ['employee', 'job', 'submitted', 'moveInDate', 'approver', 'approvedOn', 'isApproved'];
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(10);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const fetchProjects = async () => {
    if (!msalInstance || !activeAccount) return;
    setProjectLoading(true);
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('project/clayco-sei');
      let options = [];
      if (Array.isArray(response)) {
        options = response.map(p => ({ label: buildProjectOptionLabel(p.name, p.number), value: p.number, ...p }));
      } else if (Array.isArray(response?.items)) {
        options = response.items.map(p => ({ label: buildProjectOptionLabel(p.name, p.number), value: p.number, ...p }));
      }
      options.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
      setProjectOptions(options);
    } catch {
      setProjectOptions([]);
    } finally {
      setProjectLoading(false);
    }
  };

  // Save gating: require project and move-in date.
  const hasProjectSelection = Array.isArray(formData.projectAssignment)
    ? formData.projectAssignment.length > 0
    : !!formData.projectAssignment;
  const canSave = hasProjectSelection && !!formData.moveInDate;

  const householdItemsSchema = yup.object().shape({
    projectAssignment: yup
      .array()
      .of(yup.object().shape({ value: yup.mixed().required() }))
      .min(1, 'Project Assignment is required')
      .required('Project Assignment is required'),
    moveInDate: yup.string()
      .required('Move In Date is required')
      .test('valid-date', 'Move In Date is invalid', val => !val || !isNaN(new Date(val).getTime())),
  });

  // No duration validation.
  const validateDuration = () => null;

  // Use householditems endpoints; API expects moveInDate only.
  const submitHouseholdItems = async (requestBody) => {
    if (!msalInstance || !activeAccount) {
      setPeriods(prev => [...prev, { ...requestBody, localOnly: true }]);
      return { warning: 'MSAL instance/account missing; added locally only.' };
    }
    const api = new ApiProvider(msalInstance, activeAccount);
    const response = await api.Post('householditems', requestBody);
    if (response?.error) {
      const err = response.error;
      let msg = err.message || 'Error submitting Household Items period.';
      if (err.code === 409) msg = 'Submission overlaps with an existing pending/approved period.';
      if (err.details && Array.isArray(err.details)) {
        const mapped = {};
        err.details.forEach(d => { if (d.field) mapped[d.field] = d.issue || msg; });
        setFieldErrors(prev => ({ ...prev, ...mapped }));
      }
      alert(msg);
      return { error: msg };
    }
    return response;
  };

  const fetchMyHouseholdItems = async () => {
    setIsLoading(true); setLoadError('');
    try {
      if (!msalInstance || !activeAccount) {
        setLoadError('Authentication required.');
        setPeriods([]);
        return;
      }
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('householditems/me');
      if (response?.error) {
        setLoadError(response.error.message || 'Failed to load household items data.');
        setPeriods([]);
        return;
      }
      const items = Array.isArray(response?.items) ? response.items : [];
      setPeriods(
        items.map(item => ({
          id: item.id,
          employeeNumber: item.employeeNumber,
            firstName: item.firstName,
            lastName: item.lastName,
            projectName: item.projectName,
            projectNumber: item.projectNumber,
            moveInDate: item.moveInDate || item.startDate,
            status: item.isApproved === true ? 'approved' : item.isApproved === false ? 'rejected' : 'pending',
            approverName: item.approverName,
            approvedOn: item.approvedOn,
            isActive: item.isActive
        }))
      );
    } catch (err) {
      setLoadError('Failed to load household items data.');
      setPeriods([]);
    } finally {
      setIsLoading(false);
    }
  };

  const putHouseholdItems = async (requestBody) => {
    if (!msalInstance || !activeAccount) {
      alert('Authentication required.');
      return { error: 'No auth' };
    }
    const api = new ApiProvider(msalInstance, activeAccount);
    if (!requestBody?.id) {
      return { error: 'Missing id for update.' };
    }
    const response = await api.Put('householditems/', requestBody);
    if (response?.error) {
      const err = response.error;
      let msg = err.message || 'Error updating Household Items period.';
      if (err.code === 409) msg = 'Update overlaps with an existing pending/approved period.';
      if (err.details && Array.isArray(err.details)) {
        const mapped = {};
        err.details.forEach(d => { if (d.field) mapped[d.field] = d.issue || msg; });
        setFieldErrors(prev => ({ ...prev, ...mapped }));
      }
      alert(msg);
      return { error: msg };
    }
    return response;
  };

  const savePeriod = async (validData) => {
    if (!validData.projectAssignment || (Array.isArray(validData.projectAssignment) && validData.projectAssignment.length === 0)) {
      setFieldErrors(prev => ({ ...prev, projectAssignment: 'Project Assignment is required' }));
      return;
    }
    // Single date only.
    setIsSubmitting(true);
    try {
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d)) return dateStr;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };
      const project = Array.isArray(validData.projectAssignment) ? validData.projectAssignment[0] : validData.projectAssignment;
      const projectNumber = project?.value || '';
      const projectName = project?.name || '';
      const req = {
        projectNumber,
        projectName,
        moveInDate: formatDate(validData.moveInDate),
        approverEmail: project?.concurApprover || '',
      };
      if (editMode && editId) {
        req.id = editId;
        const apiResult = await putHouseholdItems(req);
        if (!apiResult?.error) {
          await Promise.all([fetchMyHouseholdItems(), fetchReviewHouseholdItems()]);
          closeModal();
        }
      } else {
        const apiResult = await submitHouseholdItems(req);
        if (!apiResult?.error) {
          await Promise.all([fetchMyHouseholdItems(), fetchReviewHouseholdItems()]);
          closeModal();
        }
      }
    } finally { setIsSubmitting(false); }
  };

  const handleFormError = (errObj) => {
    if (!errObj) return;
    if (Array.isArray(errObj)) {
      const mapped = {};
      errObj.forEach(e => { if (e?.path) mapped[e.path] = e.message; });
      setFieldErrors(mapped);
    } else setFieldErrors(errObj);
  };
  const resetForm = () => {
    setFormData({ projectAssignment: [], moveInDate: '' });
    setFieldErrors({});
    setIsSubmitting(false);
    setIsDirty(false);
    setEditMode(false);
    setEditId(null);
  };
  const openModal = () => { resetForm(); setShowModal(true); };
  const openEditModal = async (record) => {
    if (!projectOptions || projectOptions.length === 0) {
      try { await fetchProjects(); } catch {}
    }
    const recNumber = String(record.projectNumber || record.projectAssignment || '').trim();
    const recName = String(record.projectName || '').trim();
    let existingOpt = projectOptions.find(opt => String(opt.value).trim() === recNumber);
    if (!existingOpt && recName) {
      existingOpt = projectOptions.find(opt => String(opt.name || opt.projectName || '').trim().toLowerCase() === recName.toLowerCase());
    }
    if (!existingOpt && recNumber) {
      existingOpt = { label: buildProjectOptionLabel(recName, recNumber), value: recNumber, name: recName, number: recNumber, concurApprover: record.approverEmail || record.approverName || record.concurApprover || '' };
    }
    setFormData({ projectAssignment: existingOpt ? [existingOpt] : [], moveInDate: record.moveInDate || record.startDate || '' });
    setEditMode(true);
    setEditId(record.id);
    setFieldErrors({});
    setIsSubmitting(false);
    setIsDirty(false);
    setShowModal(true);
  };
  const closeModal = () => setShowModal(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveMessage, setApproveMessage] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [approvingLoading, setApprovingLoading] = useState(false);
  const openApproveModal = id => { setApprovingId(id); setApproveMessage(''); setShowApproveModal(true); };
  const handleApproveSubmit = async () => {
    if (!msalInstance || !activeAccount) return alert('Authentication required.');
    // Self-approval guard
    const target = pendingApprovals.find(item => item.id === approvingId);
    if (target && isSelfApproval(employeeNumber?.number, target.employeeNumber)) {
      const api = new ApiProvider(msalInstance, activeAccount);
      notifySelfApproval(api, { employeeNumber: target.employeeNumber, employeeName: `${target.firstName || ''} ${target.lastName || ''}`.trim(), submissionId: target.id, benefitType: 'HouseholdItems' });
      alert(SELF_APPROVAL_ERROR_MSG);
      return;
    }
    try {
      setApprovingLoading(true);
      const api = new ApiProvider(msalInstance, activeAccount);
  await api.Post(`householditems/${approvingId}/approve`, { Message: approveMessage, Notify: true });
      setPendingApprovals(prev => prev.filter(item => item.id !== approvingId));
      setShowApproveModal(false); setApprovingId(null); setApproveMessage('');
      fetchReviewHouseholdItems(); fetchMyHouseholdItems();
    } catch { alert('Failed to approve record.'); } finally { setApprovingLoading(false); }
  };
  const [rejectingLoading, setRejectingLoading] = useState(false);
  const openRejectModal = id => { setRejectingId(id); setRejectMessage(''); setShowRejectModal(true); };
  const handleRejectSubmit = async () => {
    if (!msalInstance || !activeAccount) return alert('Authentication required.');
    // Self-approval guard (also blocks self-rejection)
    const rejectTarget = pendingApprovals.find(item => item.id === rejectingId);
    if (rejectTarget && isSelfApproval(employeeNumber?.number, rejectTarget.employeeNumber)) {
      const api = new ApiProvider(msalInstance, activeAccount);
      notifySelfApproval(api, { employeeNumber: rejectTarget.employeeNumber, employeeName: `${rejectTarget.firstName || ''} ${rejectTarget.lastName || ''}`.trim(), submissionId: rejectTarget.id, benefitType: 'HouseholdItems' });
      alert(SELF_APPROVAL_ERROR_MSG);
      return;
    }
    try {
      setRejectingLoading(true);
      const api = new ApiProvider(msalInstance, activeAccount);
  await api.Post(`householditems/${rejectingId}/deny`, { Message: rejectMessage, Notify: true });
      setPendingApprovals(prev => prev.filter(item => item.id !== rejectingId));
      setShowRejectModal(false); setRejectingId(null); setRejectMessage('');
      fetchReviewHouseholdItems(); fetchMyHouseholdItems();
    } catch { alert('Failed to reject record.'); } finally { setRejectingLoading(false); }
  };

  const fetchReviewHouseholdItems = async () => {
    if (!msalInstance || !activeAccount) { setPendingApprovals([]); return; }
    const api = new ApiProvider(msalInstance, activeAccount);
    const response = await api.Get('householditems/review');
    if (response?.error) { setPendingApprovals([]); return; }
    const items = Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : [response];
    setPendingApprovals(items.map(item => ({
      id: item.id,
      employeeNumber: item.employeeNumber,
      firstName: item.firstName,
      lastName: item.lastName,
      projectName: item.projectName,
      projectNumber: item.projectNumber,
      moveInDate: item.moveInDate || item.startDate,
      status: item.isApproved === true ? 'approved' : item.isApproved === false ? 'rejected' : 'pending',
      approverName: item.approverName,
      approvedOn: item.approvedOn,
      isActive: item.isActive,
      submittedOn: item.submittedOn,
      isApproved: item.isApproved
    })));
  };

  useEffect(() => { if (typeof setMenuOptions === 'function') setMenuOptions('HouseholdItemsForm'); }, []);
  useEffect(() => { if (msalInstance && activeAccount) { fetchMyHouseholdItems(); fetchReviewHouseholdItems(); fetchProjects(); fetchApprovalHistory(); } }, [msalInstance, activeAccount]);
  useEffect(() => {
    if (!editMode) return; if (!Array.isArray(formData.projectAssignment) || formData.projectAssignment.length === 0) return; const current = formData.projectAssignment[0]; if (!current || !current.value) return; const matched = projectOptions.find(opt => String(opt.value) === String(current.value)); if (matched && matched !== current) { setFormData(prev => ({ ...prev, projectAssignment: [matched] })); }
  }, [projectOptions, editMode, formData.projectAssignment]);

  // =====================
  // Approval History Helpers & Fetch
  // =====================
  const toMMDDYYYY = (dt) => {
    if (!dt || dt === '0001-01-01T00:00:00') return '';
    const cleaned = String(dt).replace(/"+$/, '');
    const match = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
  };

  const formatHistoryRow = (item) => ({
    employee: `${item.firstName || ''} ${item.lastName || ''}`.trim(),
    job: `${item.projectName || ''}${item.projectNumber ? ' (' + item.projectNumber + ')' : ''}`.trim(),
    submitted: toMMDDYYYY(item.submittedOn),
    moveInDate: toMMDDYYYY(item.moveInDate || item.startDate),
    approver: item.approverName || '',
    approvedOn: toMMDDYYYY(item.approvedOn),
    isApproved: item.isApproved === true ? 'Yes' : item.isApproved === false ? 'No' : ''
  });

  const fetchApprovalHistory = async () => {
    if (!msalInstance || !activeAccount) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      const response = await api.Get('HouseholdItems/history');
      const items = Array.isArray(response?.items) ? response.items : [];
      const sorted = items.slice().sort((a, b) => new Date(b.approvedOn) - new Date(a.approvedOn));
      setHistoryItems(sorted);
      setHistoryPage(1);
    } catch {
      setHistoryItems([]);
      setHistoryError('Failed to load approval history.');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <HouseholdItemsPolicyPanel successMessage={successMessage} isLoading={isLoading} loadError={loadError} onAdd={openModal}>
        <PendingApprovalsList
          pendingApprovals={pendingApprovals}
          currentEmployeeNumber={employeeNumber?.number}
          openApproveModal={openApproveModal}
          openRejectModal={openRejectModal}
          showApproveModal={showApproveModal}
          setShowApproveModal={setShowApproveModal}
          approveMessage={approveMessage}
          setApproveMessage={setApproveMessage}
          handleApproveSubmit={handleApproveSubmit}
          approvingLoading={approvingLoading}
          showRejectModal={showRejectModal}
          setShowRejectModal={setShowRejectModal}
          rejectMessage={rejectMessage}
          setRejectMessage={setRejectMessage}
          handleRejectSubmit={handleRejectSubmit}
          rejectingLoading={rejectingLoading}
        />
        <MySubmissionsList
          periods={periods}
          openEditModal={openEditModal}
          handleDelete={async (p) => {
            if (!window.confirm('Are you sure you want to delete this record?')) return;
            if (!msalInstance || !activeAccount) return alert('Authentication required.');
            try { const api = new ApiProvider(msalInstance, activeAccount); await api.Delete(`householditems/${p.id}`); setPeriods(prev => prev.filter(period => period.id !== p.id)); fetchMyHouseholdItems(); } catch {}
          }}
        />
      </HouseholdItemsPolicyPanel>
      <HouseholdItemsModalForm
        showModal={showModal}
        closeModal={closeModal}
        editMode={editMode}
        isSubmitting={isSubmitting}
        canSave={canSave}
        savePeriod={savePeriod}
        stayBenefitSchema={householdItemsSchema}
        handleFormError={handleFormError}
  formData={formData}
        setFormData={setFormData}
        setIsDirty={setIsDirty}
        fieldErrors={fieldErrors}
        projectOptions={projectOptions}
        projectLoading={projectLoading}
        calculateDays={() => ''}
      />

      {/* =====================
          Approval History (visible to all users)
      ===================== */}
      {(() => {
        const historyPageCount = Math.max(1, Math.ceil(historyItems.length / historyPageSize));
        const historyGridItems = historyItems
          .slice((historyPage - 1) * historyPageSize, historyPage * historyPageSize)
          .map(formatHistoryRow);
        return (
          <div style={{ marginTop: '1.5rem' }}>
            <CdsContentPanel title="Household Items Approval History" collapsible={false}>
              {historyLoading && <div>Loading approval history...</div>}
              {historyError && <div className="text-danger">{historyError}</div>}
              {!historyLoading && !historyError && historyItems.length === 0 && <div>No approval history found.</div>}

              {!historyLoading && !historyError && historyItems.length > 0 && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(1)}>First</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(historyPage - 1)}>Prev</button>
                    <span>Page {historyPage} of {historyPageCount}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPage + 1)}>Next</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPageCount)}>Last</button>
                  </div>

                  <CdsDataTable columns={historyGridColumns} dataKeys={historyDataKeys} data={historyGridItems} usePaging={false} darkMode={true} pagingControlMode={"none"} />

                  <div className="d-flex align-items-center gap-2 mt-2">
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(1)}>First</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage === 1} onClick={() => setHistoryPage(historyPage - 1)}>Prev</button>
                    <span>Page {historyPage} of {historyPageCount}</span>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPage + 1)}>Next</button>
                    <button className="btn btn-outline-secondary btn-sm" disabled={historyPage >= historyPageCount} onClick={() => setHistoryPage(historyPageCount)}>Last</button>
                  </div>
                </>
              )}
            </CdsContentPanel>
          </div>
        );
      })()}
    </div>
  );
};

export default HouseholdItemsForm;
